import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionCreators } from 'src/reducers/common/trainings';
import { actionCreators as topicsActionCreators } from 'src/reducers/common/topics';
import {
  Box,
  Button,
  Card,
  Collapse,
  Container,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Page from 'src/components/Page';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { Delete, ExpandLess, ExpandMore, OpenInNew } from '@mui/icons-material';
import translate from 'src/helpers/translate';
import GeoGebraApplet, { getGeoGebraMaterialId } from 'src/components/common/GeoGebraApplet';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    //paddingBottom: theme.spacing(3),
    //paddingTop: theme.spacing(3)
  },
  trainingForm: {
    '& > *': {
      margin: theme.spacing(1),
      width: '25ch',
    },
    marginLeft: 20
  }
}));

const TrainingsView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const user = useSelector(state => state.userContext.user);
  const canEdit = user && user.roles && user.roles.includes('Admin');
  const topics = useSelector(state => state.common.topics);
  const allTopics = [{ id: 0 }, ...topics];
  const trainings = useSelector(state => state.common.trainings);

  const [backdropOpen, setBackdropOpen] = useState(trainings.length == 0);
  const [selectedTopicId, setSelectedTopicId] = useState(0);

  // Which training is expanded to show its embedded GeoGebra applet.
  const [expandedId, setExpandedId] = useState(null);

  let trainingsForTopic = trainings;
  if (selectedTopicId != 0) {
    trainingsForTopic = trainings.filter(tr => selectedTopicId && tr.topicId.toString().startsWith(selectedTopicId));
  }

  const [description, setDescription] = useState('');

  const [link, setLink] = useState('');

  const canBeSubmitted = description != '' && link != '';

  const handleAdd = () => {
    if (canBeSubmitted) {
      setLink('');
      setDescription('');
      dispatch(actionCreators.addTraining(description, link, selectedTopicId));
    }
  }

  const handleCloseBackdrop = () => {
    setBackdropOpen(false);
  };

  const handleDelete = (id) => {
    dispatch(actionCreators.deleteTraining(id));
  }

  const onInnerTopicChange = (event) => {
    setSelectedTopicId(event.target.value);
  }

  useEffect(() => {
    dispatch(actionCreators.requestTrainings());
    dispatch(topicsActionCreators.requestTopics()).then(async () => await setBackdropOpen(false));
  }, [dispatch])

  return (
    <Page
      className={classes.root}
      title="Trainings"
    >
      <Container maxWidth={false}>
        <Box mt={3}>
          <Backdrop className={classes.backdrop} open={backdropOpen} onClick={handleCloseBackdrop}>
            <CircularProgress color="inherit" />
          </Backdrop>
          {topics && topics.length > 0 && user && (
            <React.Fragment>
              <Card>
                <form className={classes.trainingForm}>
                  {canEdit && (
                    <React.Fragment>
                      <TextField required id="standard-required" value={description} label={translate("Description")} onChange={(event) => setDescription(event.target.value)} />
                      <TextField required id="standard-required" value={link} label={translate("Link")} onChange={(event) => setLink(event.target.value)} />
                    </React.Fragment>
                  )}
                  <FormControl>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={selectedTopicId}
                      onChange={onInnerTopicChange}
                    >
                      {allTopics.map(innerTopic => (
                        <MenuItem key={`innerTopic_${innerTopic.id}`} value={innerTopic.id}>{translate(`TopicResources_${innerTopic.id}`)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {canEdit && (
                    <Button disabled={!canBeSubmitted} onClick={handleAdd} style={{ marginTop: 20 }} variant="standard" color="primary" href="#outlined-buttons">
                      {translate("Add")}
                    </Button>
                  )}
                </form>
                <List component="nav">
                  {trainingsForTopic.map(training => {
                    const materialId = getGeoGebraMaterialId(training.url);
                    const isExpanded = expandedId === training.id;
                    return (
                      <React.Fragment key={`TrainingFor_${training.id}`}>
                        <ListItem>
                          {materialId ? (
                            <Button
                              onClick={() => setExpandedId(isExpanded ? null : training.id)}
                              endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                            >
                              <ListItemText primary={training.name} />
                            </Button>
                          ) : (
                            <Button href={training.url} target="_blank" rel="noopener noreferrer">
                              <ListItemText primary={training.name} />
                            </Button>
                          )}
                          <Tooltip title={translate('Open') || 'Open in GeoGebra'}>
                            <IconButton component="a" href={training.url} target="_blank" rel="noopener noreferrer" size="large">
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canEdit && (
                            <IconButton onClick={() => handleDelete(training.id)} size="large">
                              <Delete />
                            </IconButton>
                          )}
                        </ListItem>
                        {materialId && (
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, pb: 2 }}>
                              <GeoGebraApplet materialId={materialId} />
                            </Box>
                          </Collapse>
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Card>
            </React.Fragment>
          )}
        </Box>
      </Container>
    </Page>
  );
};

export default TrainingsView;
